import React from "react";
import { createPortal } from "react-dom";
import { useT } from "../i18n";

const PAGE_MM: Record<string, { w: number; h: number }> = {
  A4: { w: 210, h: 297 },
  Letter: { w: 215.9, h: 279.4 },
};

function pageDimsMm(pageSize: string, orientation: string): { w: number; h: number } {
  const base = PAGE_MM[pageSize] || PAGE_MM.A4;
  return orientation === "landscape"
    ? { w: base.h, h: base.w }
    : { w: base.w, h: base.h };
}

function chunk<T>(arr: T[], size: number): T[][] {
  if (size <= 0) return [arr];
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

export interface QrItem {
  id: string;
  svg: string;
  label: string;
}

interface QrPrintModalProps {
  items: QrItem[];
  columns?: number;
  rows?: number;
  width?: number;
  pageSize?: string;
  orientation?: string;
  margin?: number;
  gap?: number;
  onClose: () => void;
}

export default function QrPrintModal({
  items,
  columns = 3,
  rows = 0,
  width = 350,
  pageSize = "A4",
  orientation = "portrait",
  margin = 10,
  gap = 4,
  onClose,
}: QrPrintModalProps) {
  const t = useT();
  if (!items || items.length === 0) return null;

  const cols = Math.max(1, columns);
  const rowsPerPage = Math.max(0, rows);
  const paged = rowsPerPage > 0;
  const pages = paged ? chunk(items, cols * rowsPerPage) : [items];

  const dims = pageDimsMm(pageSize, orientation);
  const contentW = Math.max(0, dims.w - margin * 2);
  const contentH = Math.max(0, dims.h - margin * 2);

  const pageCss = `@media print { @page { size: ${pageSize} ${orientation}; margin: ${margin}mm; } }`;

  const card = (it: QrItem) => (
    <div
      key={it.id}
      className="qr-card border rounded-md p-2 flex items-center justify-center break-inside-avoid"
    >
      <div
        style={{ width: "100%", maxWidth: `${width}px` }}
        dangerouslySetInnerHTML={{ __html: it.svg }}
      />
    </div>
  );

  return createPortal(
    <div className="qr-print-overlay fixed inset-0 z-50 bg-white overflow-auto">
      <style>{pageCss}</style>
      <div className="qr-no-print sticky top-0 z-10 flex items-center justify-between gap-2 bg-gray-100 border-b px-4 py-2">
        <h2 className="font-bold text-gray-800 flex items-center gap-2">
          <i className="fa-solid fa-qrcode text-gray-500" aria-hidden="true" />
          {t("qr.title", { count: String(items.length) })}
        </h2>
        <div className="flex items-center gap-2">
          <button
            className="inline-flex items-center gap-2 bg-indigo-600 text-white rounded-md px-3 py-2 hover:bg-indigo-700 transition-colors"
            onClick={() => window.print()}
          >
            <i className="fa-solid fa-print" aria-hidden="true" />
            <span>{t("qr.print")}</span>
          </button>
          <button
            className="inline-flex items-center gap-2 bg-gray-600 text-white rounded-md px-3 py-2 hover:bg-gray-700 transition-colors"
            onClick={onClose}
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
            <span>{t("qr.close")}</span>
          </button>
        </div>
      </div>

      {paged ? (
        <div className="qr-pages flex flex-col items-center gap-4 p-4">
          {pages.map((pageItems, pi) => (
            <div
              key={pi}
              className="qr-page bg-white shadow-sm border"
              style={{
                width: `${contentW}mm`,
                height: `${contentH}mm`,
                display: "grid",
                gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                gridTemplateRows: `repeat(${rowsPerPage}, minmax(0, 1fr))`,
                gap: `${gap}mm`,
              }}
            >
              {pageItems.map(card)}
            </div>
          ))}
        </div>
      ) : (
        <div
          className="p-4 grid"
          style={{
            gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
            gap: `${gap}mm`,
          }}
        >
          {items.map(card)}
        </div>
      )}
    </div>,
    document.body
  );
}
